import React, { useEffect, useState, useCallback, useContext } from "react";
import { PermissionsContext } from "../../utils/PermissionsContext";
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, Select, MenuItem, FormControl, InputLabel,
  Switch, FormControlLabel, Tooltip, CircularProgress, Alert, Collapse,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WifiIcon from "@mui/icons-material/Wifi";
import WaterIcon from "@mui/icons-material/Water";
import BuildIcon from "@mui/icons-material/Build";
import api from "../../utils/api";

/* ── Constants ──────────────────────────────────────────────────────────── */
const SLIP_STATUS_COLORS = {
  available:   { bg: "rgba(22,163,74,0.12)",  color: "#15803d", label: "Available" },
  reserved:    { bg: "rgba(47,99,173,0.12)",  color: "#1d4ed8", label: "Reserved" },
  occupied:    { bg: "rgba(234,88,12,0.12)",  color: "#c2410c", label: "Occupied" },
  maintenance: { bg: "rgba(107,119,140,0.12)",color: "#374151", label: "Maintenance" },
  inactive:    { bg: "rgba(55,65,81,0.1)",    color: "#6b7280", label: "Inactive" },
};

const DOCK_TYPES = ["floating", "fixed", "finger", "transient", "fuel"];

/* ── Shared styles ──────────────────────────────────────────────────────── */
const primaryBtnSx = {
  backgroundColor: "#2f63ad", color: "#fff", fontWeight: 700, borderRadius: "10px",
  textTransform: "none", boxShadow: "none",
  "&:hover": { backgroundColor: "#1f4f94", boxShadow: "none" },
};
const secondaryBtnSx = { borderRadius: "10px", textTransform: "none", fontWeight: 600 };
const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#fff" } };
const dialogPaperSx = { borderRadius: "18px", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" };

/* ── Slip status chip ───────────────────────────────────────────────────── */
const SlipStatusChip = ({ status }) => {
  const s = SLIP_STATUS_COLORS[status] || SLIP_STATUS_COLORS.inactive;
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{ fontWeight: 700, fontSize: "0.72rem", height: 22, borderRadius: "6px", backgroundColor: s.bg, color: s.color }}
    />
  );
};

/* ── Dock dialog ────────────────────────────────────────────────────────── */
const BLANK_DOCK = { name: "", description: "", dock_type: "floating", sort_order: 1, is_active: true };

const DockDialog = ({ open, editing, marinaId, onClose, onSaved }) => {
  const [form, setForm] = useState(BLANK_DOCK);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setForm(editing ? { ...BLANK_DOCK, ...editing, is_active: Boolean(editing.is_active ?? 1) } : BLANK_DOCK);
      setErr("");
    }
  }, [open, editing]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) { setErr("Dock name is required."); return; }
    setSaving(true);
    try {
      const payload = { ...form, marina_id: marinaId, is_active: form.is_active ? 1 : 0 };
      if (editing?.id) await api.put(`/marina-docks/${editing.id}`, payload);
      else await api.post("/marina-docks", payload);
      onSaved();
      onClose();
    } catch { setErr("Failed to save dock."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ fontWeight: 700, pt: 3, pb: 1 }}>{editing ? "Edit Dock" : "Add Dock"}</DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1 }}>
        {err && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{err}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={8}>
            <TextField fullWidth label="Dock Name *" value={form.name} onChange={set("name")} sx={fieldSx} />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth label="Sort Order" type="number" value={form.sort_order} onChange={set("sort_order")} sx={fieldSx} />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth sx={fieldSx}>
              <InputLabel>Dock Type</InputLabel>
              <Select value={form.dock_type} label="Dock Type" onChange={set("dock_type")}>
                {DOCK_TYPES.map((t) => <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Description" multiline rows={2} value={form.description} onChange={set("description")} sx={fieldSx} />
          </Grid>
          {editing && (
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} color="success" />}
                label="Active"
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, gap: 1.25 }}>
        <Button onClick={onClose} variant="outlined" sx={secondaryBtnSx} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" sx={primaryBtnSx} disabled={saving}>
          {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : editing ? "Update" : "Add Dock"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── Slip dialog ────────────────────────────────────────────────────────── */
const BLANK_SLIP = {
  slip_number: "", slip_name: "", max_length: "", max_beam: "", max_draft: "",
  power_30amp: false, power_50amp: false, power_100amp: false,
  water: false, wifi: false, pump_out: false,
  daily_rate: "", monthly_rate: "", annual_rate: "",
  notes: "", status: "available", is_active: true,
};

const SlipDialog = ({ open, editing, dockId, docks, onClose, onSaved }) => {
  const [form, setForm] = useState(BLANK_SLIP);
  const [activeDockId, setActiveDockId] = useState(dockId);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setActiveDockId(editing?.dock_id ?? dockId);
      setForm(editing
        ? {
            ...BLANK_SLIP, ...editing,
            power_30amp: Boolean(editing.power_30amp), power_50amp: Boolean(editing.power_50amp),
            power_100amp: Boolean(editing.power_100amp), water: Boolean(editing.water),
            wifi: Boolean(editing.wifi), pump_out: Boolean(editing.pump_out),
            is_active: Boolean(editing.is_active ?? 1),
          }
        : BLANK_SLIP);
      setErr("");
    }
  }, [open, editing, dockId]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const toggle = (k) => () => setForm((p) => ({ ...p, [k]: !p[k] }));

  const handleSave = async () => {
    if (!form.slip_number.trim()) { setErr("Slip number is required."); return; }
    if (!activeDockId) { setErr("Please select a dock."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        dock_id: activeDockId,
        power_30amp: form.power_30amp ? 1 : 0,
        power_50amp: form.power_50amp ? 1 : 0,
        power_100amp: form.power_100amp ? 1 : 0,
        water: form.water ? 1 : 0,
        wifi: form.wifi ? 1 : 0,
        pump_out: form.pump_out ? 1 : 0,
        is_active: form.is_active ? 1 : 0,
        max_length: form.max_length || null,
        max_beam: form.max_beam || null,
        max_draft: form.max_draft || null,
        daily_rate: form.daily_rate || null,
        monthly_rate: form.monthly_rate || null,
        annual_rate: form.annual_rate || null,
      };
      if (editing?.id) await api.put(`/marina-slips/${editing.id}`, payload);
      else await api.post("/marina-slips", payload);
      onSaved();
      onClose();
    } catch { setErr("Failed to save slip."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ fontWeight: 700, pt: 3, pb: 1 }}>{editing ? "Edit Slip" : "Add Slip"}</DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1 }}>
        {err && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{err}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Dock selector */}
          <Grid item xs={6}>
            <FormControl fullWidth sx={fieldSx}>
              <InputLabel>Dock *</InputLabel>
              <Select value={activeDockId ?? ""} label="Dock *" onChange={(e) => setActiveDockId(e.target.value)}>
                {docks.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <TextField fullWidth label="Slip Number *" value={form.slip_number} onChange={set("slip_number")} sx={fieldSx} />
          </Grid>
          <Grid item xs={3}>
            <TextField fullWidth label="Slip Name" value={form.slip_name} onChange={set("slip_name")} sx={fieldSx} />
          </Grid>

          {/* Dimensions */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>Dimensions (ft)</Typography>
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth label="Max Length" type="number" value={form.max_length} onChange={set("max_length")} sx={fieldSx} />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth label="Max Beam" type="number" value={form.max_beam} onChange={set("max_beam")} sx={fieldSx} />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth label="Max Draft" type="number" value={form.max_draft} onChange={set("max_draft")} sx={fieldSx} />
          </Grid>

          {/* Amenities */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>Amenities</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {[
                { key: "power_30amp", label: "30A Power" },
                { key: "power_50amp", label: "50A Power" },
                { key: "power_100amp", label: "100A Power" },
                { key: "water", label: "Water" },
                { key: "wifi", label: "WiFi" },
                { key: "pump_out", label: "Pump Out" },
              ].map(({ key, label }) => (
                <FormControlLabel
                  key={key}
                  control={<Switch size="small" checked={form[key]} onChange={toggle(key)} color="primary" />}
                  label={<Typography variant="body2">{label}</Typography>}
                />
              ))}
            </Box>
          </Grid>

          {/* Rates */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>Rates</Typography>
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth label="Daily Rate ($)" type="number" value={form.daily_rate} onChange={set("daily_rate")} sx={fieldSx} />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth label="Monthly Rate ($)" type="number" value={form.monthly_rate} onChange={set("monthly_rate")} sx={fieldSx} />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth label="Annual Rate ($)" type="number" value={form.annual_rate} onChange={set("annual_rate")} sx={fieldSx} />
          </Grid>

          {/* Status + Notes */}
          {editing && (
            <Grid item xs={4}>
              <FormControl fullWidth sx={fieldSx}>
                <InputLabel>Status</InputLabel>
                <Select value={form.status} label="Status" onChange={set("status")}>
                  {Object.entries(SLIP_STATUS_COLORS).map(([v, { label }]) => (
                    <MenuItem key={v} value={v}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={editing ? 8 : 12}>
            <TextField fullWidth label="Notes" multiline rows={2} value={form.notes} onChange={set("notes")} sx={fieldSx} />
          </Grid>
          {editing && (
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} color="success" />}
                label="Active"
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, gap: 1.25 }}>
        <Button onClick={onClose} variant="outlined" sx={secondaryBtnSx} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" sx={primaryBtnSx} disabled={saving}>
          {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : editing ? "Update" : "Add Slip"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── Slip amenity icons ─────────────────────────────────────────────────── */
const AmenityIcons = ({ slip }) => (
  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
    {slip.power_30amp ? <Chip label="30A" size="small" sx={{ height: 18, fontSize: "0.68rem", fontWeight: 700, backgroundColor: "rgba(234,88,12,0.1)", color: "#c2410c" }} /> : null}
    {slip.power_50amp ? <Chip label="50A" size="small" sx={{ height: 18, fontSize: "0.68rem", fontWeight: 700, backgroundColor: "rgba(234,88,12,0.1)", color: "#c2410c" }} /> : null}
    {slip.power_100amp ? <Chip label="100A" size="small" sx={{ height: 18, fontSize: "0.68rem", fontWeight: 700, backgroundColor: "rgba(234,88,12,0.1)", color: "#c2410c" }} /> : null}
    {slip.water ? <Tooltip title="Water"><WaterIcon sx={{ fontSize: 16, color: "#0891b2" }} /></Tooltip> : null}
    {slip.wifi ? <Tooltip title="WiFi"><WifiIcon sx={{ fontSize: 16, color: "#7c3aed" }} /></Tooltip> : null}
    {slip.pump_out ? <Tooltip title="Pump Out"><BuildIcon sx={{ fontSize: 16, color: "#6b778c" }} /></Tooltip> : null}
  </Box>
);

/* ── Main component ─────────────────────────────────────────────────────── */
const MarinaDocksSlips = ({ marinaId, showSnack }) => {
  const perms = useContext(PermissionsContext);
  const canManage = perms.includes("marina.manage_slips");

  const [docks, setDocks] = useState([]);
  const [slipsByDock, setSlipsByDock] = useState({});
  const [expandedDock, setExpandedDock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dockDialog, setDockDialog] = useState({ open: false, editing: null });
  const [slipDialog, setSlipDialog] = useState({ open: false, editing: null, dockId: null });

  const fetchDocks = useCallback(async () => {
    try {
      const res = await api.get(`/marina-docks?marina_id=${marinaId}`);
      const list = res.data || [];
      setDocks(list);
      if (list.length > 0 && !expandedDock) setExpandedDock(list[0].id);
    } catch { showSnack("Failed to load docks", "error"); }
    finally { setLoading(false); }
  }, [marinaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSlips = useCallback(async (dockId) => {
    try {
      const res = await api.get(`/marina-slips?dock_id=${dockId}`);
      setSlipsByDock((prev) => ({ ...prev, [dockId]: res.data || [] }));
    } catch { showSnack("Failed to load slips", "error"); }
  }, [showSnack]);

  useEffect(() => { fetchDocks(); }, [fetchDocks]);

  useEffect(() => {
    if (expandedDock && !slipsByDock[expandedDock]) fetchSlips(expandedDock);
  }, [expandedDock, slipsByDock, fetchSlips]);

  const handleDeleteDock = async (id) => {
    if (!window.confirm("Delete this dock? All associated slips must be removed first.")) return;
    try {
      await api.delete(`/marina-docks/${id}`);
      showSnack("Dock deleted");
      fetchDocks();
    } catch { showSnack("Failed to delete dock", "error"); }
  };

  const handleDeleteSlip = async (id, dockId) => {
    if (!window.confirm("Delete this slip?")) return;
    try {
      await api.delete(`/marina-slips/${id}`);
      showSnack("Slip deleted");
      fetchSlips(dockId);
    } catch { showSnack("Failed to delete slip", "error"); }
  };

  const toggleDock = (dockId) => {
    setExpandedDock((prev) => (prev === dockId ? null : dockId));
    if (!slipsByDock[dockId]) fetchSlips(dockId);
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
          Docks & Slips
        </Typography>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} sx={primaryBtnSx}
            onClick={() => setDockDialog({ open: true, editing: null })}>
            Add Dock
          </Button>
        )}
      </Box>

      {docks.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: "12px" }}>No docks found. Add one to get started.</Alert>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {docks.map((dock) => {
            const isExpanded = expandedDock === dock.id;
            const slips = slipsByDock[dock.id] || [];
            return (
              <Paper
                key={dock.id}
                elevation={0}
                sx={{ borderRadius: "14px", border: "1px solid rgba(47,99,173,0.10)", boxShadow: "0 4px 14px rgba(0,0,0,0.04)", overflow: "hidden" }}
              >
                {/* Dock header */}
                <Box
                  onClick={() => toggleDock(dock.id)}
                  sx={{ display: "flex", alignItems: "center", px: 2.5, py: 2, cursor: "pointer", backgroundColor: isExpanded ? "rgba(47,99,173,0.04)" : "#fff", userSelect: "none", "&:hover": { backgroundColor: "rgba(47,99,173,0.04)" } }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>
                        {dock.name}
                      </Typography>
                      <Chip label={dock.dock_type || "N/A"} size="small" sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600, backgroundColor: "rgba(47,99,173,0.08)", color: "#2f63ad" }} />
                      {!dock.is_active && <Chip label="Inactive" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />}
                    </Box>
                    <Typography variant="caption" sx={{ color: "#6b778c" }}>
                      {dock.slip_count ?? slips.length} slips · {dock.available_slips ?? "N/A"} available
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                    {canManage && (
                      <Tooltip title="Add slip to this dock">
                        <IconButton size="small" sx={{ color: "#2f63ad" }} onClick={() => setSlipDialog({ open: true, editing: null, dockId: dock.id })}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canManage && (
                      <IconButton size="small" sx={{ color: "#2f63ad" }} onClick={() => setDockDialog({ open: true, editing: dock })}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    {canManage && (
                      <IconButton size="small" color="error" onClick={() => handleDeleteDock(dock.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" sx={{ color: "#6b778c" }}>
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                </Box>

                {/* Slips table */}
                <Collapse in={isExpanded}>
                  <Divider />
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#5f6b7a" }}>Slip</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#5f6b7a" }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#5f6b7a" }}>Dimensions (ft)</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#5f6b7a" }}>Amenities</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#5f6b7a" }}>Daily / Monthly</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#5f6b7a" }}>Vessel</TableCell>
                          {canManage && <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#5f6b7a" }}>Actions</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {slips.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} sx={{ textAlign: "center", py: 3, color: "#9aa5b4" }}>
                              No slips yet. Click + above to add one.
                            </TableCell>
                          </TableRow>
                        ) : slips.map((slip) => (
                          <TableRow key={slip.id} hover sx={{ "&:last-child td": { border: 0 } }}>
                            <TableCell sx={{ fontWeight: 700, color: "#223142" }}>{slip.slip_number}</TableCell>
                            <TableCell><SlipStatusChip status={slip.status} /></TableCell>
                            <TableCell sx={{ fontSize: "0.8rem", color: "#445" }}>
                              {slip.max_length ? `${slip.max_length} × ${slip.max_beam ?? "N/A"} × ${slip.max_draft ?? "N/A"}` : "N/A"}
                            </TableCell>
                            <TableCell><AmenityIcons slip={slip} /></TableCell>
                            <TableCell sx={{ fontSize: "0.8rem", color: "#445" }}>
                              {slip.daily_rate ? `$${slip.daily_rate}/day` : "N/A"}{slip.monthly_rate ? ` · $${slip.monthly_rate}/mo` : ""}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8rem", color: "#6b778c" }}>
                              {slip.current_vessel_name || "N/A"}
                            </TableCell>
                            {canManage && (
                              <TableCell align="right">
                                <IconButton size="small" sx={{ color: "#2f63ad" }} onClick={() => setSlipDialog({ open: true, editing: slip, dockId: dock.id })}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => handleDeleteSlip(slip.id, dock.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Dialogs */}
      <DockDialog
        open={dockDialog.open}
        editing={dockDialog.editing}
        marinaId={marinaId}
        onClose={() => setDockDialog({ open: false, editing: null })}
        onSaved={() => { fetchDocks(); showSnack(dockDialog.editing ? "Dock updated" : "Dock added"); }}
      />
      <SlipDialog
        open={slipDialog.open}
        editing={slipDialog.editing}
        dockId={slipDialog.dockId}
        docks={docks}
        onClose={() => setSlipDialog({ open: false, editing: null, dockId: null })}
        onSaved={() => {
          const dockId = slipDialog.editing?.dock_id ?? slipDialog.dockId;
          fetchSlips(dockId);
          showSnack(slipDialog.editing ? "Slip updated" : "Slip added");
        }}
      />
    </Box>
  );
};

export default MarinaDocksSlips;
